import { CRS, LatLng, Point } from "leaflet";

export interface OverlapMarker {
  id: string;
  position: LatLng;
}

export interface ProjectedOverlapMarker extends OverlapMarker {
  point: Point;
}

const MAX_LOCATION_DISTANCE_METERS = 50;
const MAX_SCREEN_DISTANCE_PIXELS = 24;
const MIN_MARKER_SPACING_PIXELS = 14;

export const compactMarkerPosition = (
  original: LatLng,
  spread: LatLng,
  markerDiameter: number
) => {
  const scale = markerDiameter / MIN_MARKER_SPACING_PIXELS;
  return new LatLng(
    original.lat + (spread.lat - original.lat) * scale,
    original.lng + (spread.lng - original.lng) * scale
  );
};

const markerGroups = (markers: ProjectedOverlapMarker[]) => {
  const parents = markers.map((_, index) => index);
  const buckets = new Map<string, number[]>();
  const find = (index: number): number => {
    if (parents[index] !== index) {
      parents[index] = find(parents[index]);
    }
    return parents[index];
  };
  const union = (left: number, right: number) => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) {
      parents[rightRoot] = leftRoot;
    }
  };

  markers.forEach((marker, index) => {
    const x = Math.floor(marker.point.x / MAX_SCREEN_DISTANCE_PIXELS);
    const y = Math.floor(marker.point.y / MAX_SCREEN_DISTANCE_PIXELS);

    for (let xOffset = -1; xOffset <= 1; xOffset += 1) {
      for (let yOffset = -1; yOffset <= 1; yOffset += 1) {
        const candidates = buckets.get(`${x + xOffset}:${y + yOffset}`) || [];
        candidates.forEach((candidateIndex) => {
          const candidate = markers[candidateIndex];
          if (
            CRS.Earth.distance(marker.position, candidate.position) <=
              MAX_LOCATION_DISTANCE_METERS &&
            marker.point.distanceTo(candidate.point) <=
              MAX_SCREEN_DISTANCE_PIXELS
          ) {
            union(index, candidateIndex);
          }
        });
      }
    }

    const key = `${x}:${y}`;
    buckets.set(key, [...(buckets.get(key) || []), index]);
  });

  const groups = new Map<number, ProjectedOverlapMarker[]>();
  markers.forEach((marker, index) => {
    const root = find(index);
    groups.set(root, [...(groups.get(root) || []), marker]);
  });
  return groups.values();
};

export const spreadMarkerPoints = (markers: ProjectedOverlapMarker[]) => {
  const spread = new Map<string, Point>();

  for (const group of markerGroups(markers)) {
    if (group.length === 1) {
      spread.set(group[0].id, group[0].point);
      continue;
    }

    const ordered = [...group].sort((left, right) =>
      left.id.localeCompare(right.id)
    );
    const center = group
      .reduce((point, marker) => point.add(marker.point), new Point(0, 0))
      .divideBy(group.length);
    const radius =
      MIN_MARKER_SPACING_PIXELS /
      (2 * Math.sin(Math.PI / group.length));

    ordered.forEach((marker, index) => {
      const angle = -Math.PI / 2 + (index * 2 * Math.PI) / ordered.length;
      spread.set(
        marker.id,
        center.add(
          new Point(Math.cos(angle) * radius, Math.sin(angle) * radius)
        )
      );
    });
  }

  return spread;
};